import React, { useEffect, useState, useRef } from "react";
import { Card, Dropdown, Menu, Typography, Spin, Empty } from "antd";
import { DownOutlined, LoadingOutlined } from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { dashboardPMSApis } from "../../../api/DashboardPMS";

const { Text } = Typography;

// Module-level cache to persist across remounts
const fetchCache = {
  fetchedIds: new Set<string>(),
  fetchingIds: new Set<string>(),
  dataCache: new Map<string, any>(),
};

const WeeklyDigestCard: React.FC<any> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [weekData, setWeekData] = useState<any>(null);
  const [mockWeeklyDigestData, setMockWeeklyDigestData] = useState<any>(null);
  const [dropdownData, setDropdownData] = useState<any[]>([]);
  const [selectedWeek, setSelectedWeek] = useState<any>();
  const [selectedWeekRange, setSelectedWeekRange] = useState<{
    weekStart: string;
    weekEnd: string;
  } | null>(null);

  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchWeeksDropDown = async () => {
    const cacheKey = String(id);

    // Check if already fetched or currently fetching
    if (fetchCache.fetchedIds.has(cacheKey) || fetchCache.fetchingIds.has(cacheKey)) {
      // Use cached data if available
      const cachedData = fetchCache.dataCache.get(cacheKey);
      if (cachedData && isMountedRef.current) {
        setDropdownData(cachedData.dropdownData);
        setSelectedWeek(cachedData.selectedWeek);
        setSelectedWeekRange(cachedData.selectedWeekRange);
        setLoading(false);
      }
      return;
    }

    // Mark as fetching
    fetchCache.fetchingIds.add(cacheKey);

    try {
      const apiResponse: ApiResponse =
        await dashboardPMSApis.fetchWeeklyDigestDropdown(id);
      const { data, status } = apiResponse;

      // Mark as fetched and remove from fetching
      fetchCache.fetchedIds.add(cacheKey);
      fetchCache.fetchingIds.delete(cacheKey);

      if (status && data) {
        // Cache the data
        fetchCache.dataCache.set(cacheKey, {
          dropdownData: data,
          selectedWeek: data[0]?.label,
          selectedWeekRange: {
            weekStart: data[0]?.weekStart,
            weekEnd: data[0]?.weekEnd,
          },
        });

        if (isMountedRef.current) {
          setDropdownData(data);
          setSelectedWeek(data[0]?.label);
          setSelectedWeekRange({
            weekStart: data[0]?.weekStart,
            weekEnd: data[0]?.weekEnd,
          });
          setLoading(false);
        }
      } else {
        if (isMountedRef.current) {
          setDropdownData([]);
          setLoading(false);
        }
      }
    } catch (error) {
      console.log(error);
      fetchCache.fetchingIds.delete(cacheKey);
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };
  const fetchWeeklyDigest = async () => {
    try {
      const apiResponse: ApiResponse = await dashboardPMSApis.fetchWeeklyDigest(
        id,
        "weeklyDigest",
        selectedWeek && selectedWeekRange?.weekStart
          ? selectedWeekRange?.weekStart
          : null,
        selectedWeek && selectedWeekRange?.weekEnd
          ? selectedWeekRange?.weekEnd
          : null
      );
      const { data, status } = apiResponse;
      setTimeout(() => {
        if (status && data) {
          setMockWeeklyDigestData(data);
          setWeekData(data);
        } else {
          setMockWeeklyDigestData(null);
        }
        setLoading(false);
      }, 800);
    } catch (error) {
      console.log(error);
    }
  };
  useEffect(() => {
    if (id) {
      fetchWeeksDropDown();
    }
  }, [id]);

  const handleMenuClick = (e: any) => {
    const selected = dropdownData.find((item) => item.label === e.key);
    setSelectedWeek(selected.label);
    setSelectedWeekRange({
      weekStart: selected.weekStart,
      weekEnd: selected.weekEnd,
    });
    setLoading(true);
  };

  useEffect(() => {
    // Only fetch when selectedWeek is set (after dropdown data is loaded)
    if (selectedWeek && selectedWeekRange) {
      fetchWeeklyDigest();
    }
  }, [selectedWeek, selectedWeekRange]);

  const menu = (
    <Menu onClick={handleMenuClick}>
      {dropdownData?.map((item: any) => (
        <Menu.Item key={item.label}>{item.label}</Menu.Item>
      ))}
    </Menu>
  );

  const getChartOptions = () => {
    if (!weekData) return {};
    const categories = Object.keys(weekData);
    const created = categories.map((cat) => weekData[cat].created);
    const completed = categories.map((cat) => weekData[cat].completed);
    const open = categories.map((cat) => weekData[cat].open);

    return {
      tooltip: {
        trigger: "axis",
      },
      legend: {
        data: ["Created", "Completed", "Still Open"],
        top: 10,
      },
      grid: {
        top: 60,
        bottom: 25,
        left: 20,
        right: 10,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        name: "Category",
        nameLocation: "middle",
        nameGap: 30,
      },
      yAxis: {
        type: "value",
        name: "Task Count",
        nameLocation: "middle",
        nameGap: 30,
      },
      series: [
        {
          name: "Created",
          type: "bar",
          data: created,
          itemStyle: { color: "#1890ff" },
        },
        {
          name: "Completed",
          type: "bar",
          data: completed,
          itemStyle: { color: "#52c41a" },
        },
        {
          name: "Still Open",
          type: "bar",
          data: open,
          itemStyle: { color: "#faad14" },
        },
      ],
    };
  };

  return (
    <Card
      title={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Weekly Digest</span>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Text style={{ fontSize: 13 }}>Select Week</Text>
            <Dropdown overlay={menu} trigger={["click"]}>
              <a
                onClick={(e) => e.preventDefault()}
                style={{
                  fontSize: 12,
                  padding: "2px 8px",
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                  display: "inline-flex",
                  alignItems: "center",
                  height: 24,
                }}
              >
                {selectedWeek || "Select Week"}{" "}
                <DownOutlined style={{ fontSize: 10, marginLeft: 4 }} />
              </a>
            </Dropdown>
          </div>
        </div>
      }
    >
      {loading ? (
        <div
          style={{
            height: 260,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        </div>
      ) : !weekData || Object.keys(weekData).length === 0 ? (
        <div
          style={{
            height: 260,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Empty description="No data found" />
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 10 }}>
            <Text>
              From <Text strong>{selectedWeekRange?.weekStart}</Text> To{" "}
              <Text strong>{selectedWeekRange?.weekEnd}</Text>
            </Text>
          </div>
          <ReactECharts
            style={{ height: 260, width: "100%" }}
            option={getChartOptions()}
            notMerge={true}
            lazyUpdate={true}
          />
        </>
      )}
    </Card>
  );
};

export default WeeklyDigestCard;
